/**
 * CAN A BLAST CATCH THE ONE WHO THREW IT?
 *
 * The defect this exists to settle: a melee weapon with Blast is centred on
 * somebody you are standing next to, so its radius covers you every single
 * time. Left alone, the app has you grenade yourself on every swing.
 *
 * The ruling that fixes it without pretending mishaps cannot happen is
 * INTENT: you are not in the shape you AIMED at, but scatter - the failure of
 * intent - can put you inside it. A melee blast never scatters, so a melee
 * attacker is never caught, which is exactly the case that prompted this.
 *
 * Allies are never exempt under any of these. Catching your own squad is a
 * matter of execution, and the app should let you make that mistake.
 *
 * Pure, zero React dependency.
 */

export type BlastSelfPolicy = 'onScatter' | 'never' | 'always';

export const DEFAULT_BLAST_SELF: BlastSelfPolicy = 'onScatter';

export interface BlastSelfEntry {
  key: BlastSelfPolicy;
  label: string;
  hint: string;
}

/** The rulings on offer, in the order the GM meets them. */
export const BLAST_SELF_POLICIES: BlastSelfEntry[] = [
  {
    key: 'onScatter',
    label: 'Only if it scatters onto them',
    hint: 'You are not in the template you aimed at, but a grenade that goes astray can land at your feet. A melee blast never scatters, so a melee attacker is never caught by their own swing.',
  },
  {
    key: 'never',
    label: 'Never',
    hint: 'The thrower is always outside their own blast, however badly it goes. Simplest at the table, and nobody has to argue about a mishap.',
  },
  {
    key: 'always',
    label: 'Whenever they are inside it',
    hint: 'Strictest reading: if the template covers you, it covers you. Expect melee blast weapons to hurt the person swinging them, every time.',
  },
];

export function isBlastSelfPolicy(value: unknown): value is BlastSelfPolicy {
  return value === 'onScatter' || value === 'never' || value === 'always';
}

/**
 * Is the attacker inside their own template this time?
 *
 * The one place the ruling is read, so the three-way choice cannot be
 * half-applied by a caller that forgot the scatter case.
 */
export function blastCatchesAttacker(policy: BlastSelfPolicy, didScatter: boolean): boolean {
  if (policy === 'always') return true;
  if (policy === 'never') return false;
  return didScatter;
}
